/**
 * @fileoverview Hook Forms Layer para gestión y validación del formulario de evento de cliente (actividad).
 * Separa la lógica de validación y estado del formulario del componente UI.
 * @module useCustomerEventForm
 * @category Hook - Forms Layer
 * @since 1.0.0
 * @version 1.0.0
 */

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

/**
 * Hook Forms Layer para inicializar y validar el formulario de evento de cliente.
 *
 * @param {Object} customer - Objeto de cliente (debe tener web_id)
 * @param {number} maxChars - Máximo de caracteres para la descripción
 * @returns {{ formMethods: Object, validationSchema: Object }}
 *
 * @example
 * const { formMethods } = useCustomerEventForm(customer, 80);
 *
 * @see ManageCustomerEventModalMC
 */
export default function useCustomerEventForm(customer) {
  const { t } = useTranslation('InboxMC');
  const maxChars = 80;

  // Validación de formulario (memoizada)
  const validationSchema = useMemo(() =>
    Yup.object().shape({
      subcategory: Yup.string().required(t('event.category') + ' ' + t('chat.required')),
      action: Yup.string()
        .required(t('chat.required'))
        .max(maxChars, t('chat.max_chars', { max: maxChars })),
    }),
    [t, maxChars]
  );

  // Formulario react-hook-form
  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      web_id: customer.web_id,
      subcategory: '',
      action: ''
    },
    mode: 'onChange'
  });

  return { formMethods, maxChars };
}
